import { later, scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { APIData, Service } from 'webapp-types';
import { SheetColumnType } from 'webapp/enums/enums';
import { TourId } from 'webapp/enums/tour';
import { TrackingEvent } from 'webapp/enums/tracking';
import { UserSettingsKey } from 'webapp/enums/user-settings';
import { sortBy } from 'webapp/utils/common';
import SheetSelection from 'webapp/utils/sheet-selection';

import type { ColorSelection } from 'webapp/components/custom-query/setup/sheet/preview';
import CustomQuerySetupWizardSheetComponent from 'webapp/components/custom-query/setup/wizard/sheet';

import type { PreviewErrorData } from 'webapp/models-custom/custom-metric-setup-form/base';
import type CustomMetricSetupFormItemField from 'webapp/models-custom/custom-metric-setup-form/item/field';
import CustomMetricSetupFormPrebuilt from 'webapp/models-custom/custom-metric-setup-form/prebuilt';
import type { DefaultTitleInfo } from 'webapp/models-custom/custom-query';
import DataSheet from 'webapp/models-custom/data-sheet';
import type StatusObject from 'webapp/models-custom/status-object';
import type Tour from 'webapp/models-custom/tour';

export default class CustomMetricSetupFormSheet extends CustomMetricSetupFormPrebuilt {
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;
	@service('tour') declare tourService: Service.Tour;
	@service('user-settings') declare userSettingsService: Service.UserSettings;

	@tracked sheets: DataSheet[] = [];
	@tracked fileUrl = '';

	override allowTransformations = false;

	wizardComponent = CustomQuerySetupWizardSheetComponent;

	metricInputNode: HTMLElement | null = null; // because of focus and tours

	override get isWizardFlowSupported(): boolean {
		// for sheet wizard, we need at least one metric column
		// and either dimension or a date
		return (
			this.columnsForMetricCount > 0 &&
			(this.columnsForDateCount > 0 || this.columnsForDimensionCount > 0)
		);
	}

	// ------- Initializing --------

	override initData(data: APIData.CustomQuery.BuilderSheetResponse) {
		this.createDataSheets(data.data);

		if (data.fileUrl) {
			this.fileUrl = data.fileUrl;
		}

		const dataForBase = {
			builder: data.builder,
			settings: data.settings,
		};

		super.initData(dataForBase);
	}

	private createDataSheets(data: APIData.CustomQuery.BuilderSheetResponse['data']): void {
		const sheetData = data || {};

		// Test data
		// multiple sheets (+ some have order)
		// all except c1 and pivot have columnTypes
		// sheetData = window.dbx_mock['sheetdata 1'];

		// formattings + is trimmed
		// sheetData = window.dbx_mock['sheetdata 2'];

		// remington sheet (hori scroll, high rows)
		// sheetData = window.dbx_mock['sheetdata 3'];

		let sheets: DataSheet[] = [];
		Object.entries(sheetData).forEach((sheet) => {
			const [id, entries] = sheet;
			sheets.push(new DataSheet(id, entries));
		});

		sheets = sortBy(sheets, 'order');
		const firstSheet = sheets[0];
		if (firstSheet) {
			firstSheet.isFirst = true;
		}

		this.sheets = sheets;
	}

	override onFormLoaded(node: HTMLElement): void {
		// Default view for new custom query is wizard view. When editing existing custom query
		// we have non-wizard view where input is visible and we can focus this input
		const metricInputNode: HTMLElement | null = node.querySelector('input[name="metric"]');

		if (metricInputNode) {
			this.metricInputNode = metricInputNode;

			if (!this.isNew) {
				metricInputNode.focus();
			}
		}

		this.markGlobalA1FocusedClass();
	}

	override onFormUnload(): void {
		super.onFormUnload();
		this.endHelpBubbles();
	}

	// ------ current form status & handlers ---------

	@tracked isSheetDataReloading = false;

	// used to check if we need to unfocus a1 fields
	@tracked isSheetDataFocused = false;
	@tracked collapserJustClicked = false;

	override get isSaveButtonDisabled(): boolean {
		return super.isSaveButtonDisabled || !this.isValid;
	}

	override handleItemAction(item: CustomMetricSetupFormItemField | string, action: string): void {
		// sheet collapser that should not change focus
		if (action === 'focus-out') {
			if (this.collapserJustClicked) {
				return;
			}
		}

		if (item === 'collapser' && action === 'click') {
			this.collapserJustClicked = true;
			return;
		}
		this.collapserJustClicked = false;

		// sheetData that should not change focus either
		if (item === 'sheetData' && action === 'focus-in') {
			this.isSheetDataFocused = true;
			return;
		}

		// don't unfocus a1 if sheet data is focused
		if (
			typeof item !== 'string' &&
			item?.isA1 &&
			action === 'focus-out' &&
			this.isSheetDataFocused
		) {
			return;
		}
		// assume that pretty much every other action unfocuses sheet
		this.isSheetDataFocused = false;

		// for bubbles
		const tour = this.bubbleTour;
		if (typeof item !== 'string' && item?.id && tour?.isActive) {
			if (item.id === 'metric') {
				if (action === 'focus-in') {
					tour.goToStep(1);
				} else if (action === 'focus-out' && tour.currentStep === 1) {
					tour.hideBubble();
				}
			} else if (item.id === 'date') {
				if (action === 'focus-in') {
					tour.goToStep(2);
				} else if (action === 'focus-out' && tour.currentStep === 2) {
					this.endHelpBubbles();
				}
			}
		}

		super.handleItemAction(item, action);
	}

	override onContentChange(): void {
		scheduleOnce('afterRender', this, this.repositionBubbleTour);
	}

	// -------- form data & preview ---------

	reloadData(): void {
		this.isSheetDataReloading = true;
		this.fetchBuilder(true)
			.then((reply) => {
				this.createDataSheets((reply as APIData.CustomQuery.BuilderSheetResponse).data);
			})
			.finally(() => {
				this.isSheetDataReloading = false;
			})
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
	}

	// -------- validation & error handling ---------

	get sheetNamesForValidation(): string[] {
		return this.sheets.map((item) => item.id);
	}

	override handlePreviewErrorFromStatusObjects(statusObjects: StatusObject[]): void {
		const dataErrorText = statusObjects
			.map((item) => item.statusTitleAndDescription)
			.join('<br>');

		if (dataErrorText) {
			this.handlePreviewError({ html: dataErrorText });
		}
	}

	override handlePreviewError(errorData: PreviewErrorData<string>) {
		const message = errorData.html || this.getFormattedError(errorData.errors || [], true);

		this.errors = [
			{
				name: 'general',
				message,
			},
		];
	}

	// -------- field statuses ---------

	get columnsForMetricCount(): number {
		let columnsForMetricCount = 0;
		(this.sheets || []).forEach((sheet) => {
			columnsForMetricCount += sheet.columnsForMetric.length;
		});
		return columnsForMetricCount;
	}

	get columnsForDateCount(): number {
		let columnsForDateCount = 0;
		(this.sheets || []).forEach((sheet) => {
			columnsForDateCount += sheet.columnsForDate.length;
		});
		return columnsForDateCount;
	}

	get columnsForDimensionCount(): number {
		let columnsForDimensionCount = 0;
		(this.sheets || []).forEach((sheet) => {
			columnsForDimensionCount += sheet.columnsForDimension.length;
		});
		return columnsForDimensionCount;
	}

	protected override onCurrentlyFocusedFieldChange(): void {
		this.markGlobalA1FocusedClass();
	}

	// -------- A1 field specifics --------

	get currentlyFocusedA1(): CustomMetricSetupFormItemField | null {
		let field;
		if (this.currentlyFocusedField?.isA1) {
			field = this.currentlyFocusedField;
		}
		return field || null;
	}

	get a1Fields(): CustomMetricSetupFormItemField[] {
		// works only for first layer items!
		return (this.items as CustomMetricSetupFormItemField[]).filter((item) => item.isA1);
	}

	get colorSelections(): ColorSelection[] {
		const info: ColorSelection[] = [];
		this.a1Fields.forEach((field) => {
			const color = this.getColorForA1Field(field.id as 'metric' | 'date' | 'dimension'),
				range = field.value as string;

			if (color && range && SheetSelection.fromA1(range).isValid) {
				info.push({
					color: color,
					range: range,
				});
			}
		});
		return info;
	}

	onA1Selected(selection: string): void {
		if (this.currentlyFocusedA1) {
			this.currentlyFocusedA1.value = selection;
			this.currentlyFocusedA1.isValid = this.currentlyFocusedA1.validate();
		}
	}

	getA1FieldForType(type: string) {
		return this.a1Fields.find((item) => item.id === type);
	}

	getColorForA1Field(fieldId: 'metric' | 'date' | 'dimension'): string {
		const colors = {
			metric: '#52b19d', // @color_sheet_metric
			date: '#ff9000', // @color_sheet_date
			dimension: '#6dcff6', // @color_sheet_dimension
		};

		return colors[fieldId];
	}

	getColumnTitleForFieldType(fieldType: string): string {
		// arg SheetColumnType
		const selectedValue = this.getA1FieldForType(fieldType)?.value as string;

		if (selectedValue) {
			const sheetSelection = SheetSelection.fromA1(selectedValue);
			if (sheetSelection.isWholeCols) {
				const selectedSheet = this.sheets.find(
					(item) => item.sheetPrefix === sheetSelection.a1Prefix,
				);
				if (selectedSheet) {
					const columnId = selectedValue.split(':')[0]!,
						columnTitle = selectedSheet.getColumnTitle(columnId);
					if (columnTitle) {
						return columnTitle;
					}
				}
			}
		}

		return '';
	}

	getMetricNameForSelectedFields(): string {
		let name = 'My metric';

		const defaultTitleInfo = this.getDefaultTitleInfo();

		if (defaultTitleInfo?.metric) {
			name = defaultTitleInfo.metric;
		}

		if (defaultTitleInfo?.dimension) {
			name += ` by ${defaultTitleInfo.dimension}`;
		}

		return name;
	}

	getDefaultTitleInfo(): DefaultTitleInfo | null {
		const defaultTitleInfo: DefaultTitleInfo = {};

		const colTitleMetric = this.getColumnTitleForFieldType(SheetColumnType.METRIC);

		if (colTitleMetric) {
			defaultTitleInfo.metric = colTitleMetric;
		}

		const colTitleDimension = this.getColumnTitleForFieldType(SheetColumnType.DIMENSION);

		if (colTitleDimension) {
			defaultTitleInfo.dimension = colTitleDimension;
		}

		return defaultTitleInfo;
	}

	markGlobalA1FocusedClass(): void {
		$('body').toggleClass('a1focused', !!this.currentlyFocusedA1);
	}

	// -------- tacking etc ----------

	onWizardFlowToggle(isWizardFlow: boolean): void {
		if (!isWizardFlow && this.isNew) {
			this.initTourForQbGSheetsVideo();
		}

		const eventName = isWizardFlow
			? TrackingEvent.QUERY_SWITCH_TO_WIZARD
			: TrackingEvent.QUERY_SWITCH_TO_MANUAL;
		this.analyticsService.event(eventName, { type: this.query.trackingWizardType });
	}

	// -------- bubbles tour ----------

	bubbleTour: Tour | null = null;

	startHelpBubbles(): void {
		if (this.tourService.isEligibleForQbSheetsTour) {
			if (this.bubbleTour) {
				this.bubbleTour.hideBubble();
				this.bubbleTour = null;
			}

			const metricInputNode = this.metricInputNode;
			if (metricInputNode) {
				metricInputNode.focus();
			}

			const tour = this.tourService.initTour(TourId.QB_SHEETS);
			if (tour) {
				tour.start(); // bubble marked as done where there are at least two custom sheets metrics created
				this.bubbleTour = tour;
			}
		}
	}

	initTourForQbGSheetsVideo(): void {
		const isLessThanTwoGSheetsCustomMetrics =
			this.mmCustomQueriesService.customQueryModels.filter((item) => item.isSheets).length <
			2;
		const isEligibleForQbSheetsVideoAccordingCustomeMetricsCount =
			!this.userSettingsService.hasSeenCustomMetricsGSheetsVideo &&
			isLessThanTwoGSheetsCustomMetrics;
		const startHelpBubbles = () => {
			later(() => {
				this.startHelpBubbles();
			}, 400);
		};

		if (
			!this.tourService.isEligibleForQbSheetsVideoTour &&
			!isEligibleForQbSheetsVideoAccordingCustomeMetricsCount
		) {
			startHelpBubbles();
			return;
		}

		const tour = this.tourService.initTour(TourId.QB_SHEETS_VIDEO, {
			afterMarkDone: () => {
				void this.userSettingsService.save(
					UserSettingsKey.CUSTOM_METRICS_QB_GSHEETS_VIDEO_SEEN,
					true,
				);
				startHelpBubbles();
			},
		});

		if (tour) {
			tour.start();
		}
	}

	repositionBubbleTour() {
		const tour = this.bubbleTour;
		if (tour) {
			tour.repositionBubble();
		}
	}

	endHelpBubbles(): void {
		const tour = this.bubbleTour;
		if (tour) {
			// tour.end(); // Removed end because it marks tour done but we have condition defined in tour QbSheetsTour
			this.bubbleTour = null;
		}
	}
}
